import { type Href, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Card from '@/components/UI/Card';
import CheckboxRow from '@/components/UI/CheckboxRow';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import Text from '@/components/Unistyles/Text';
import TextInput from '@/components/Unistyles/TextInput';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
} from '@/components/Welcome/WelcomeUI';
import type { WelcomeFormValues } from '@/layouts/Welcome/form';
import { MAXIMUM_EVENT_TITLE_LENGTH } from '@/utils/constants';

type WelcomeOrgLeagueSeasonProps = {
  nextRoute?: Href;
};

export default function WelcomeOrgLeagueSeason({
  nextRoute = '/welcome/org/teams',
}: WelcomeOrgLeagueSeasonProps) {
  const { navigate } = useRouter();
  const { control, setValue } = useFormContext<WelcomeFormValues>();
  const organizationTitle = useWatch({ control, name: 'organizationTitle' });
  const leagueTitle = useWatch({ control, name: 'leagueTitle' });
  const seasonTitle = useWatch({ control, name: 'seasonTitle' });
  const hasStandings = useWatch({ control, name: 'seasonHasStandings' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const trimmedLeagueTitle = leagueTitle.trim();
  const trimmedSeasonTitle = seasonTitle.trim();
  const isValid =
    trimmedLeagueTitle.length > 0 && trimmedSeasonTitle.length > 0;
  const hierarchy = [
    {
      id: 'organization',
      title: organizationTitle.trim() || 'Organization',
    },
    {
      id: 'league',
      title: trimmedLeagueTitle || 'Main League',
    },
    {
      id: 'season',
      title: trimmedSeasonTitle || `${DateTime.now().year} Season`,
    },
  ];

  const onPressContinue = () => {
    if (!trimmedLeagueTitle) {
      setErrorMessage('League name is required.');
      return;
    }

    if (!trimmedSeasonTitle) {
      setErrorMessage('Season name is required.');
      return;
    }

    navigate(nextRoute);
  };

  return (
    <WelcomeStepFrame
      title="Create your first League and Season"
      footer={
        <WelcomeActions>
          <WelcomePrimaryAction
            label="Continue"
            disabled={!isValid}
            onPress={onPressContinue}
          />
        </WelcomeActions>
      }
    >
      <WelcomeStepScrollContent>
        <WelcomeCard tone="success">
          <Box space="xs">
            <Text size="sm" bold variant="typographySecondary">
              Hierarchy
            </Text>
            <Box direction="horizontal" alignItems="center" wrap space="xs">
              {hierarchy.map((item, index) => (
                <Box
                  key={item.id}
                  direction="horizontal"
                  alignItems="center"
                  space="xs"
                >
                  {index > 0 ? (
                    <Icon
                      name="arrow-forward-outline"
                      size="lg"
                      variant="typographySecondary"
                    />
                  ) : null}
                  <Text size="lg" bold>
                    {item.title}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        </WelcomeCard>

        <WelcomeCard>
          <Box space="xs">
            <Text size="sm" bold variant="typographySecondary">
              League
            </Text>
            <TextInput
              autoCapitalize="words"
              enterKeyHint="done"
              maxLength={MAXIMUM_EVENT_TITLE_LENGTH}
              placeholder="League name"
              value={leagueTitle}
              onChangeText={(value) => {
                setValue('leagueTitle', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
            />
          </Box>

          <Box space="xs">
            <Text size="sm" bold variant="typographySecondary">
              Season
            </Text>
            <TextInput
              autoCapitalize="words"
              enterKeyHint="done"
              maxLength={MAXIMUM_EVENT_TITLE_LENGTH}
              placeholder="Season name"
              value={seasonTitle}
              onChangeText={(value) => {
                setValue('seasonTitle', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
            />
          </Box>

          {errorMessage ? (
            <Text variant="error" center selectable>
              {errorMessage}
            </Text>
          ) : null}
        </WelcomeCard>

        <Card w="full" background="surface">
          <CheckboxRow
            value={hasStandings}
            onValueChange={(nextHasStandings) => {
              setValue('seasonHasStandings', nextHasStandings, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            variant="success"
            spaced
          >
            <Text bold flexShrink={1}>
              Track standings for this season.
            </Text>
          </CheckboxRow>
        </Card>
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}
